import h from 'hyperscript';

import { logger } from '@disy/lib/logging';
import type { MenuContentElement } from '@disy/ui/menu';

import type { ConditionElement } from 'cadenza/conditions/condition-element';
import type { QlConditionController } from 'cadenza/conditions/ql-condition/ql-condition-controller';
import type { ConditionType } from 'cadenza/model/condition-type.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { isHidden } from 'cadenza/workbook/conditions/condition-util';
import { WorkbookConditionWrapper } from 'cadenza/workbook/conditions/workbook-condition-wrapper';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import { ConditionSettingsKey } from 'cadenza/workbook/model/condition-settings-key.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { getActiveWorksheetId } from 'cadenza/workbook/workbook-core-selectors.state';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';

import './create-condition.css';

// TODO (to be optimized with migration of date-time-condition.js)
interface DateTimeCondition extends ConditionElement {
  adjustConditionSettings: () => Promise<object>;
  updateConditionSettings: (settings: object) => void;
}

type createConditionFn<T extends ConditionType> = (
  condition: ConditionDto<T>,
  dataViewId: DataViewId,
  options: ConditionCreationOptions,
) => Promise<void | HTMLElement>;

interface ConditionCreationOptions {
  affectedMapViews: { id: string; text: string }[];
  getQlConditionControllerFn: (condition: ConditionDto) => QlConditionController;
}

// Type is close to Partial<Record<ConditionType, createConditionFn>>, but allows createConditionFn
// to assume a specific ConditionDto subtype.
const CONDITION_COMPONENT_FUNCTION_PROVIDER: { [K in ConditionType]?: createConditionFn<K> } = {
  TableCondition: async (
    condition: ConditionDto<'TableCondition'>,
    dataViewId: DataViewId,
    options: ConditionCreationOptions,
  ) => {
    const { createTableCondition } = await import('./table-condition-component-provider');
    return createTableCondition(condition, dataViewId, options);
  },
  LikeCondition: async (
    condition: ConditionDto<'LikeCondition'>,
    dataViewId: DataViewId,
    _: ConditionCreationOptions,
  ) => {
    const { createLikeCondition } = await import('./like-condition-component-provider');
    return createLikeCondition(condition, dataViewId);
  },
  DateCondition: async (condition: ConditionDto, dataViewId: DataViewId, options: ConditionCreationOptions) => {
    const { createDateCondition } = await import('./date-condition-component-provider');
    return createDateCondition(condition, dataViewId, options.affectedMapViews);
  },
  DateIntervalCondition: async (condition: ConditionDto, dataViewId: DataViewId, options: ConditionCreationOptions) => {
    const { createDateCondition } = await import('./date-condition-component-provider');
    return createDateCondition(condition, dataViewId, options.affectedMapViews);
  },
  IpAddressCondition: async (condition: ConditionDto<'IpAddressCondition'>, dataViewId: DataViewId, _) => {
    const { createIpAddressCondition } = await import('./ip-address-condition-component-provider');
    return createIpAddressCondition(condition, dataViewId);
  },
  ValueIntervalCondition: async (
    condition: ConditionDto<'ValueIntervalCondition'>,
    dataViewId: DataViewId,
    _: ConditionCreationOptions,
  ) => {
    const { createIntervalCondition } = await import('./value-interval-condition-component-provider');
    return createIntervalCondition(condition, dataViewId);
  },
  QlCondition: async (condition: ConditionDto, dataViewId: DataViewId, options: ConditionCreationOptions) => {
    const { createQlCondition } = await import('./ql-condition-component-provider');
    return createQlCondition(condition, dataViewId, options);
  },
  TreeCondition: async (condition: ConditionDto<'TreeCondition'>, dataViewId: DataViewId, _) => {
    const { createTreeCondition } = await import('./tree-condition-component-provider');
    return createTreeCondition(condition, dataViewId);
  },
  PolygonCondition: async (condition: ConditionDto, _: DataViewId, __: ConditionCreationOptions) => {
    const { createSpatialCondition } = await import('./spatial-condition-component-provider');
    return createSpatialCondition(condition);
  },
};

/**
 * Creates a condition element to be added in a panel (analyse context or local filters).
 *
 * @param condition - The condition model for which to create the condition
 * @param dataViewId - Id of the data view it applies to (for local conditions)
 * @param withAffectedViews - If true the widget that shows the affected views is displayed
 * @param initiallyFocused - Should it be focused
 * @param menuActionsProvider - If provided, a menu is added in the header using the actions provided by this function
 * @param getQlConditionControllerFn - Provider for the QL condition controller
 * @param affectedMapViews - Array of {id, name} objects taken from map views that are affected by this condition
 * @param conditionDisabled - If true the condition is disabled. Condition is enabled if this is false, and it meets other criteria
 * @param conditionAlwaysVisible - If true the condition is always visible regardless of its hidden condition settings.
 * @return - A condition web component wrapped in a <li> tag and an additional wrapper
 *   containing the header with the title, reset button, menu etc.
 */
export function createCondition<T extends ConditionType>(
  condition: ConditionDto<T>,
  {
    dataViewId,
    getQlConditionControllerFn,
    withAffectedViews = true,
    initiallyFocused = false,
    menuActionsProvider,
    affectedMapViews = [],
    conditionDisabled = false,
  }: {
    dataViewId?: DataViewId;
    getQlConditionControllerFn: (condition: ConditionDto) => QlConditionController;
    withAffectedViews?: boolean;
    initiallyFocused?: boolean;
    menuActionsProvider?: (conditionId: ConditionId) => MenuContentElement[];
    affectedMapViews?: { id: string; text: string }[];
    conditionDisabled?: boolean;
    conditionAlwaysVisible?: boolean;
  },
) {
  // Cloning, because the condition elements mutate these objects.
  condition = structuredClone(condition);
  const isInvalid = condition.status !== 'VALID';
  const conditionRequired = getSetting<boolean>(
    condition,
    ConditionSettingsKey.toPersistenceKey('CONDITION_MANDATORY'),
  );

  // At first we use just a placeholder, until we dynamically import necessary conditionComponentProvider.
  let conditionComponentWrapperContainer: HTMLElement = createConditionPlaceholder(
    condition,
    isInvalid,
    conditionDisabled,
  );

  const conditionType = condition.type;
  const providerFn = CONDITION_COMPONENT_FUNCTION_PROVIDER[conditionType];
  if (!providerFn) {
    throw new Error(`Could not find provider for ${conditionType}`);
  }
  providerFn(condition, dataViewId!, { getQlConditionControllerFn, affectedMapViews })
    .then(async (result) => {
      const conditionComponent = result as ConditionElement;
      // Update the condition settings if necessary when the attribute settings have changed.
      const updatedConditionSettings =
        condition.type === 'DateCondition' ?
          await (conditionComponent as DateTimeCondition).adjustConditionSettings()
        : null;
      if (updatedConditionSettings) {
        // If the settings have changed, only update the conditions settings but don't initialize
        // the condition any further because than the condition might be applied with invalid settings.
        // Example: condition has smaller date granularity than the attribute.
        (conditionComponent as DateTimeCondition).updateConditionSettings(updatedConditionSettings);
      } else {
        // Once we get the condition component we update the placeholder.
        const newConditionComponentWrapperContainer = conditionComponentWrapperContainer.cloneNode(true) as HTMLElement;
        if (conditionRequired) {
          conditionComponent.setAttribute('required', String(conditionRequired));
        }
        conditionComponent.dataset.id = condition.id;
        conditionComponent.dataset.conditionType = condition.type;
        newConditionComponentWrapperContainer.append(
          new WorkbookConditionWrapper(
            condition,
            conditionComponent,
            withAffectedViews,
            initiallyFocused,
            menuActionsProvider,
            conditionDisabled,
          ),
        );

        newConditionComponentWrapperContainer.cloneNode = () =>
          createCondition(condition, {
            dataViewId,
            withAffectedViews,
            initiallyFocused,
            menuActionsProvider,
            getQlConditionControllerFn,
          });
        conditionComponentWrapperContainer.replaceWith(newConditionComponentWrapperContainer);
        conditionComponentWrapperContainer = newConditionComponentWrapperContainer;
      }
    })
    .catch(logger.error);

  return conditionComponentWrapperContainer;
}

function createConditionPlaceholder(condition: ConditionDto, isInvalid: boolean, alwaysVisible: boolean) {
  const conditionPlaceholder = h('li.li-condition-wrapper.d-hover-context.draggable-condition-element-marker', {
    'data-id': condition.id,
  });
  conditionPlaceholder.classList.toggle('is-invalid', isInvalid);
  conditionPlaceholder.toggleAttribute(
    'hidden',
    !alwaysVisible && isHidden(condition, getActiveWorksheetId(getStore().getState())),
  );
  return conditionPlaceholder;
}
